import { useMemo, useState } from "react"
import {
  Dumbbell,
  MessageCircle,
  HeartPulse,
  CalendarDays,
  Link2,
  Target,
  Utensils,
  ShoppingCart,
  LayoutGrid,
  AlertTriangle,
} from "lucide-react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import type { GeneralMetrics } from "@/lib/api"
import { NameList } from "@/components/NameList"
import { StaleBanner } from "@/components/StaleBanner"
import { cn } from "@/lib/utils"

/**
 * Engagement grouped BY FEATURE, not by event name.
 *
 * The flat "most-tapped" list answered "which event fires most", which nobody
 * asks. This answers the question we actually have — how do people reach each
 * thing we built, and what do they do once they're there — and it makes two
 * things visible that a ranked list structurally cannot:
 *
 *   1. A feature with NO tracking still gets a card. Nutrition and Grocery
 *      shipped and send nothing; in a ranked list they simply weren't there,
 *      which reads identically to "nobody used it". The empty card is the
 *      finding.
 *   2. Reach and intensity are separated, in two columns that never add up. A
 *      big average next to a small people count is one person's habit, and the
 *      two numbers sitting side by side say that on their own.
 *
 * All of it is a regroup of what `/admin/general_metrics` already returns:
 * `actions` (hand-written taps), `screens` (auto-captured), `chat_opens`
 * (which door someone came through). No new endpoint.
 */

// ---------------------------------------------------------------------------
// The map. Three kinds of signal, deliberately mixed:
//
//   action  — someone wrote a capture() call for it. Autocapture is off in the
//             app on purpose (chat copy sits in accessibility labels), so these
//             exist only where a developer added one. Silence can mean "not
//             instrumented" as easily as "not used".
//   screen  — the app reports every screen it shows, for everyone, with no code.
//             This is why screens sit in the cards rather than only in the
//             diagnostic table: without them, Goals & preferences reads dead
//             when 64 people actually open Profile. Screen = reach; tap = what
//             they did once they arrived. The gap between the two IS the read.
//   door    — the `source` on chat_opened: where someone was standing when they
//             opened the coach. Labelled "Entry —" on screen; `door` stays the
//             internal kind because it is the shape of the data, not the copy.
//   outcome — what HAPPENED: it worked, it broke, the app showed them something.
//             Not a tap, which is why it is a separate kind — and it is the kind
//             that finds breaks. Taps alone said 12 people connected a calendar;
//             8 of them ever got connected.
//
// A row may appear under two features on purpose (the book pill is both a chat
// door and a booking entry point) — it is one tap read two ways, never a sum.
// ---------------------------------------------------------------------------

type Kind = "action" | "screen" | "door" | "db" | "outcome"
// `after` pins a row next to the one it is meant to be read against, instead of
// letting the people-count sort separate the pair. `note` is for a row whose
// number is misleading without a sentence — a broken tracker, a screen that
// isn't what its name says. Better one line under the row than a footnote
// nobody connects back to it.
type RowSpec = {
  kind: Kind
  id: string
  label?: string
  sub?: boolean
  after?: string
  note?: string
  /**
   * Pins a row to the top of its card ahead of the people-sort. For the one
   * number a card exists to answer — which is often small, and would otherwise
   * sink beneath the taps that lead up to it.
   */
  lead?: boolean
  /**
   * What a `db` row counts MORE of than people, when it can hold more than one
   * each — "devices", "logins". Omitted where one person means one of them, so
   * the row says nothing rather than reporting a 1.0 average as if it were a
   * finding.
   */
  unit?: string
}
type FeatureSpec = {
  key: string
  name: string
  blurb: string
  color: string
  Icon: typeof Dumbbell
  rows: RowSpec[]
  /** Card-level caveat, under the rows. For "this whole card is off" facts. */
  note?: string
  /**
   * What an EMPTY card means, when empty is a legitimate answer rather than a
   * tracking hole. Without this, no rows renders the amber "sends us nothing"
   * state — right for a feature that shipped mute, wrong for a card whose
   * subject we deleted on purpose.
   */
  empty?: string
}

const FEATURES: FeatureSpec[] = [
  {
    key: "workouts",
    name: "Workouts",
    blurb: "live pillar · workout generation + editing",
    color: "#8b5cf6",
    Icon: Dumbbell,
    // No "opened the workout plan" row on purpose. The Workouts tab is the tab
    // the app LANDS on, so its screen count is everyone who opened the app at
    // all — that number is the header strip above, where it can't pose as a
    // feature someone chose.
    rows: [
      { kind: "screen", id: "WorkoutDetail", label: "Opened a single workout" },
      { kind: "action", id: "home_workout_opened", label: "Opened a workout from home" },
      { kind: "action", id: "exercise_toggled", label: "Checked off an exercise" },
      { kind: "action", id: "session_completion_toggled", label: "Marked a session done" },
      { kind: "action", id: "exercise_metrics_saved", label: "Logged weight or reps" },
      { kind: "action", id: "home_next_week_expanded", label: "Opened next week" },
      { kind: "action", id: "exercise_added", label: "Added an exercise" },
      { kind: "action", id: "exercise_removed", label: "Removed an exercise" },
      { kind: "action", id: "exercise_video_played", label: "Played an exercise video" },
      // Coach edits live here because the edit changes the WORKOUT. They also
      // appear under Coach chat as a cross-reference, because that is where
      // they originate.
      { kind: "screen", id: "DiffEditor", label: "Opened the coach-edit review" },
      { kind: "action", id: "diff_change_approved", label: "Accepted a coach edit" },
      { kind: "action", id: "diff_change_rejected", label: "Rejected a coach edit" },
      { kind: "outcome", id: "diff_discarded", label: "Threw away a whole coach edit" },
      { kind: "action", id: "diff_editor_residual_open", label: "Reopened an unfinished edit review" },
    ],
  },
  {
    key: "chat",
    name: "Coach chat",
    blurb: "agent surface · the conversational front door",
    color: "#f97316",
    Icon: MessageCircle,
    rows: [
      { kind: "action", id: "chat_opened", label: "Opened coach chat" },
      // The real reach number for chat. Nothing else counts a member who was
      // answered — the tap only fires on the deliberate doors below, and most
      // people just type in the composer they're already looking at.
      { kind: "outcome", id: "chat_v2_run_completed", label: "Got a coach reply back" },
      { kind: "door", id: "at_a_glance", label: "Entry — At a Glance card", sub: true },
      { kind: "door", id: "home_empty_state", label: "Entry — empty home screen", sub: true },
      { kind: "door", id: "home_book_pill", label: "Entry — book pill on a home tile", sub: true },
      {
        kind: "door",
        id: "nav_orb",
        label: "Entry — coach orb in the nav bar",
        sub: true,
        note:
          "Zero until the app build carrying the new tap ships — and nothing stands in for it meanwhile. The three entries above sum exactly to the chat-opened total, so the most obvious way into the coach was simply never counted. It will then appear as a new entry that grows for weeks, which is users updating, not a habit forming.",
      },
      // A canary, not a gap: it reads 0 because every entry point the app ships
      // today names itself. It only lifts off zero if someone adds a new route
      // into chat and forgets the `source` — which is exactly when you want to
      // hear about it, and the only way you ever would.
      {
        kind: "door",
        id: "unknown",
        label: "Entry — a route that didn't name itself",
        sub: true,
        note:
          "Should always read 0. Anything that lands here is a new way into the coach that shipped without a label — the row exists to catch that, not to measure it.",
      },
      { kind: "action", id: "chat_message_flagged", label: "Flagged a coach reply" },
      // Rows 7 + 8 of the tap-point audit. Both taps funnel through the same
      // handler in the app, so one event with a `source` covers the chips under
      // the composer AND every option inside an embedded card.
      {
        kind: "action",
        id: "chat_suggestion_tapped",
        label: "Tapped a suggestion or card option",
        note:
          "Zero until the app build carrying it ships. Today's stand-in matches message text against the chip labels — it can't tell a tap from someone typing the same four words, and can't see embedded cards at all.",
      },
      {
        kind: "action",
        id: "chat_card_action",
        label: "Confirmed or cancelled a card",
        note:
          "Zero until the app build carrying it ships. Edit review, booking confirms and profile updates are all decided here now — which is why the accept/reject rows below, still wired to the old review screen, read near zero.",
      },
      // Same events as Workouts, shown again so chat's downstream effect is
      // visible from the chat card. Never added into a total.
      { kind: "action", id: "diff_change_approved", label: "Accepted an edit it proposed", sub: true },
      { kind: "action", id: "diff_change_rejected", label: "Rejected an edit it proposed", sub: true },
    ],
  },
  {
    key: "biometrics",
    name: "Biometrics",
    blurb: "live pillar · wearables + readiness",
    color: "#ef4444",
    Icon: HeartPulse,
    rows: [
      // Postgres state, and the only row here that is a real total. It LEADS
      // rather than closing a funnel, because it is not the bottom of one: it
      // counts everyone holding a wearable today, whenever they linked it and
      // whatever build they were on, while every event row under it is capped
      // by the window and by which app builds ship the tracking. Reading it as
      // "34 got through, 13 tapped" is the trap the note exists to shut.
      {
        kind: "db",
        id: "connected_wearables",
        label: "Has a wearable linked right now",
        unit: "devices",
        note:
          "Live state, all-time, from our own database — not the bottom of the taps below. Those only count the window shown and only from builds that send the event, so they will always read lower. One person can hold a watch and a ring.",
      },
      {
        kind: "action",
        id: "wearable_connect_started",
        label: "Tapped connect a wearable",
        after: "connected_wearables",
        sub: true,
      },
      // The event side of the outcome. Cross-checks the tap above it — same
      // window, same builds — which is the only comparison here that is fair.
      {
        kind: "outcome",
        id: "wearable_connect_succeeded",
        label: "Wearable link went through",
        after: "wearable_connect_started",
        sub: true,
      },
      {
        kind: "outcome",
        id: "wearable_connect_failed",
        label: "Wearable link broke",
        after: "wearable_connect_succeeded",
        sub: true,
        note:
          "They tapped connect and ended up with nothing linked — a token or provider handshake that errored, or a return from the provider's login page with no connection made. Each one carries the stage it died at.",
      },
      // Not a feature and not a tap: the card re-reports itself whenever its
      // data changes, so the volume column is noise and only the people count
      // means anything. It is the ceiling for the two rows anchored under it.
      {
        kind: "outcome",
        id: "glance_viewed",
        label: "Had the At a Glance card in front of them",
        note:
          "The card reporting itself, not a tap — so the volume column is meaningless here (it re-fires every time the card's data changes). It should equal everyone who opened the app and doesn't, because only newer builds send it. Read it as the ceiling for the two rows below, not as use.",
      },
      {
        kind: "action",
        id: "glance_connect_tapped",
        label: "Tapped connect on At a Glance",
        after: "glance_viewed",
        sub: true,
      },
      {
        kind: "action",
        id: "glance_checkin_answered",
        label: "Answered a check-in question",
        after: "glance_connect_tapped",
        sub: true,
      },
      { kind: "screen", id: "WearableSetup", label: "Opened wearable setup" },
    ],
  },
  {
    key: "bookings",
    name: "Bookings",
    blurb: "live pillar · studio class booking",
    color: "#3b82f6",
    Icon: CalendarDays,
    rows: [
      // The point of the whole pillar, so it is pinned to the top of the card
      // instead of being sorted into the middle by its own small count. It has
      // to come from PostHog: the `bookings` table lost its history in the
      // July wipe (9 rows left, none confirmed), so Postgres cannot answer
      // "how many classes actually got booked" and this event is all there is.
      {
        kind: "outcome",
        id: "booking_completed",
        label: "Booked a class",
        lead: true,
        note:
          "A class actually reserved at a studio. Counted from the app's own event, because the bookings table was wiped in July and can no longer answer this.",
      },
      // Where a booking actually starts. Four surfaces hand the job to the
      // coach — the home class card, a retry after a failed booking, the Book
      // button inside a workout, and the rest-day studio panel — by switching
      // to the coach tab with the message pre-written. Only the home card was
      // ever counted, so this tile showed a 3-person path as if it were how
      // bookings start. A screen row used to stand in for the rest, but it also
      // caught the two paths that hand over an EDIT rather than a booking, so
      // it could never be read as a booking number; this replaces it honestly.
      {
        kind: "action",
        id: "booking_handoff",
        label: "Started a booking with the coach",
        note:
          "Not measurable yet — the tap is written and under review, but no build carrying it has shipped, so this is an unwired zero, not a behavioural one. When it does lift off zero it will climb as users UPDATE, which on a booking tile is easily mistaken for bookings growing. Don't read the early slope.",
      },
      // Was two rows: this, and the `home_book_pill` chat entry. They are one
      // tap — the same handler fires both, back to back — and both read 4 taps
      // from the same 3 people over 90 days, so the second row was the first
      // one restated. The chat reading still exists where it belongs, as an
      // entry point on the Coach chat card; a booking card should count it once.
      { kind: "action", id: "home_book_tapped", label: "Tapped book a class on a home tile" },
      // NOT "opened a studio page". This is the app handing the member the
      // studio's own site mid-booking to finish something we can't do for them
      // — a login, a captcha, a card. It is a booking that needed a human, not
      // browsing.
      {
        kind: "action",
        id: "booking_optin_browser_opened",
        label: "Sent to the studio's own site to finish a step",
        note:
          "The agent hit something only the user can clear — a login, a captcha, a payment — and handed the studio's real page over. A booking that stalled, not a page someone chose to visit.",
      },
      // A second tap on the same booking, swallowed so it can't double-charge.
      // Low is fine here; climbing means the first tap looks like it did nothing.
      {
        kind: "outcome",
        id: "booking_optin_browser_dampened",
        label: "Tapped book twice, second one ignored",
        after: "booking_optin_browser_opened",
        sub: true,
      },
      { kind: "screen", id: "BookingHistory", label: "Opened booking history" },
      // Renamed off "Cancelled a class they'd booked", which the data never
      // supported: the app has no cancel-a-reservation feature at all. The
      // event fires server-side whenever a booking SESSION stops, and 90 of the
      // 94 all-time carried a status of "waiting on the member" or "still
      // working" — an attempt dropped before any class was held. Four ever had
      // a confirmed booking behind them.
      {
        kind: "outcome",
        id: "booking_cancelled",
        label: "Stopped a booking in progress",
        note:
          "Backing out of an attempt, not undoing a reservation — we have no way to cancel a class that's already booked. All but a handful stopped before anything was reserved.",
      },
    ],
  },
  {
    key: "connections",
    name: "Connections",
    blurb: "plumbing · calendar + studio logins",
    color: "#06b6d4",
    Icon: Link2,
    rows: [
      // The OUTCOME, from `profiles.google_calendar_oauth`. It leads because
      // it is the only row here that survives a disconnect and a build change:
      // the funnel under it is made of events that only exist in builds that
      // ship them, so anyone who linked earlier is invisible to every row but
      // this one. Holding a token now is the fact; the rest is the story.
      {
        kind: "db",
        id: "google_calendar",
        label: "Has a calendar linked right now",
        lead: true,
        note:
          "The live answer, and the only one that can't drift: everyone who ever connected, minus everyone who since disconnected. The rows under it are the story of how they got here and are windowed and build-limited, so they will never reconcile to this number exactly.",
      },
      // How many people were even OFFERED it. Every event row under this is a
      // share of this one, which is why it heads the chain.
      {
        kind: "outcome",
        id: "google_calendar_tile_viewed",
        label: "Was shown the connect-calendar prompt",
        after: "google_calendar",
        sub: true,
        note:
          "The card on the home screen offering to link a calendar, appearing — not an invite we send anyone. It's the denominator: nobody below it was ever asked twice.",
      },
      {
        kind: "action",
        id: "google_calendar_connect_tapped",
        label: "Tapped connect calendar",
        after: "google_calendar_tile_viewed",
        sub: true,
      },
      {
        kind: "outcome",
        id: "google_calendar_connect_succeeded",
        label: "Calendar actually connected",
        after: "google_calendar_connect_tapped",
        sub: true,
      },
      {
        kind: "outcome",
        id: "google_calendar_connect_failed",
        label: "Hit an error connecting",
        after: "google_calendar_connect_succeeded",
        sub: true,
      },
      {
        kind: "outcome",
        id: "google_calendar_connect_external_browser",
        label: "Kicked out to Safari to finish",
        after: "google_calendar_connect_failed",
        sub: true,
      },
      // The other end of the same chain, kept directly under it so "linked
      // right now" reads as connected-minus-disconnected on one screen instead
      // of being three numbers scattered by the people-count sort.
      {
        kind: "action",
        id: "google_calendar_disconnect_tapped",
        label: "Tapped disconnect calendar",
        after: "google_calendar_connect_external_browser",
        sub: true,
      },
      {
        kind: "outcome",
        id: "google_calendar_disconnect_succeeded",
        label: "Disconnect went through",
        after: "google_calendar_disconnect_tapped",
        sub: true,
        note:
          "The tap above is the ask, this is the token actually being dropped. A gap between them is a disconnect that didn't take — the user thinks they're unlinked and we still hold the calendar.",
      },
      { kind: "action", id: "google_calendar_picker_opened", label: "Opened the calendar picker" },
      { kind: "action", id: "google_calendar_default_set", label: "Chose a default calendar" },
      {
        kind: "screen",
        id: "StudioCredentials",
        label: "Opened studio logins",
        note:
          "Only counts opens inside the window shown, and only from builds that report screens — the saved-logins row under it is live state with no window at all, which is how a 0 can sit above a 2.",
      },
      // The other side of that screen, from `site_credentials`. It answers how
      // many people finished — which the screen row never could — and stops
      // there: nothing anywhere reports whether the login actually works.
      {
        kind: "db",
        id: "studio_login",
        label: "Saved a studio login",
        after: "StudioCredentials",
        sub: true,
        // A login PER STUDIO, so these outnumber the people holding them.
        unit: "logins",
        note: "Saved one — not that it works. No studio reports a login back to us.",
      },
      // Separate path, separate number: the studio's own OAuth, not a login we
      // hold. Worth its own row precisely so a zero here stays legible against
      // the saved-logins row above rather than being folded into it.
      {
        kind: "db",
        id: "mindbody_oauth",
        label: "Linked a MindBody account",
        note: "Studio-side OAuth, separate from a saved login.",
      },
    ],
    // Said out loud because the calendar funnel above it makes studio linking
    // LOOK measured by association, and only half of it is.
    note:
      "We now see how many people SAVED a studio login, never whether it works — no studio reports that back. Unlike calendar, a broken studio login is still invisible.",
  },
  {
    key: "goals",
    name: "Account, goals & preferences",
    blurb: "what the coach is told to work toward · and staying or leaving",
    color: "#10b981",
    Icon: Target,
    rows: [
      // Leads because it is the answer this card exists for, and it is not
      // reachable any other way: goals are set by TALKING, so no screen view
      // and no tap can stand in for it.
      {
        kind: "outcome",
        id: "goal_created",
        label: "Set a goal",
        lead: true,
        note:
          "Recorded wherever the goal came from. Roughly ten of these are set in conversation with the coach for every one set on the profile screen.",
      },
      { kind: "outcome", id: "goal_updated", label: "Changed a goal", after: "goal_created", sub: true },
      { kind: "outcome", id: "goal_deleted", label: "Dropped a goal", after: "goal_created", sub: true },
      {
        kind: "screen",
        id: "Profile",
        label: "Opened their profile",
        note:
          "This IS the profile-tab tap, not a stand-in — the tab is the only live route in, so the screen view and the tap are the same number. It can exceed the 53 onboarded users because every account with a login is counted, onboarded or not.",
      },
      { kind: "action", id: "profile_section_saved", label: "Saved a profile change" },
      { kind: "screen", id: "EditSummary", label: "Edited their summary" },
      { kind: "action", id: "home_refer_friends_tapped", label: "Tapped refer a friend" },
      { kind: "screen", id: "ReferFriends", label: "Opened refer a friend", after: "home_refer_friends_tapped", sub: true },
      { kind: "action", id: "user_signed_out", label: "Signed out" },
      { kind: "action", id: "user_self_deleted_initiated", label: "Started deleting their account" },
      // The one that matters: started vs finished. A gap is someone who thought
      // about leaving and stayed.
      {
        kind: "outcome",
        id: "user_self_deleted_completed",
        label: "Went through with deleting it",
        after: "user_self_deleted_initiated",
        sub: true,
      },
    ],
    // The Goals row used to sit here reading 1 person, which invited the wrong
    // conclusion — members aren't ignoring goals, they can't reach them.
    note:
      "The standalone Goals and Analytics tabs are built but switched off since April 2026, so they can't register use — which is why goal-setting is counted from the goal itself rather than from a screen.",
  },
  {
    key: "nutrition",
    name: "Nutrition",
    blurb: "agent surface · fifth pillar",
    color: "#eab308",
    Icon: Utensils,
    rows: [],
  },
  {
    key: "grocery",
    name: "Grocery",
    blurb: "agent surface · shortlist + runs",
    color: "#a855f7",
    Icon: ShoppingCart,
    rows: [],
  },
  {
    key: "other",
    name: "Retired sign-up questions",
    blurb: "the old activity questionnaire · deleted from the app 2026-07-04",
    color: "#94a3b8",
    Icon: LayoutGrid,
    // Deliberately empty. Nothing is CLAIMED here — every row lands by falling
    // through the cards above, which is what makes the card worth reading: it
    // is the list of what we measure but haven't decided belongs anywhere.
    rows: [],
    // The rest of this card's note is composed in build(): the retired-screen
    // sentence, plus a second sentence naming anything genuinely unclassified
    // that landed here on a given tab.
    empty:
      "Nobody in this view ever saw the questionnaire — it was deleted on 2026-07-04, so only users who signed up before then have rows here. Nothing else is unaccounted for either: every screen and tap we measure landed in a card above.",
  },
]

type Row = {
  key: string
  kind: Kind
  label: string
  people: number
  total: number
  note?: string
  /** Indented: this row is read against the one above it, not on its own. */
  sub?: boolean
  /** See `lead` on RowSpec — sorts to the top of its card regardless of size. */
  lead?: boolean
  /**
   * The database read behind a `db` row failed, so there is no number — shown
   * as "—", never as 0. An event row reading 0 means people didn't do the
   * thing; a db row reading 0 would mean nobody has linked the account, and
   * saying that when we simply couldn't look is the one lie this card can't
   * afford. Only ever set on `kind: "db"`.
   */
  missing?: boolean
  /** See `unit` on RowSpec. Absent when the row has no second dimension. */
  unit?: string
  /** The members `people` counts, by name. Empty when the API sent none. */
  users: string[]
}

export function FeatureEngagement({
  g,
  users,
}: {
  g: GeneralMetrics | null
  /**
   * How many of the accounts below finished onboarding — `total_members` off
   * the onboarding endpoint, the same 53 the Accounts section and the Executive
   * Summary print. Passed in rather than re-derived: the two endpoints run the
   * same cohort filter, and a second definition of "user" living in here is
   * exactly how the dashboard would start contradicting itself.
   */
  users?: number | null
}) {
  const built = useMemo(() => build(g), [g])
  if (!g || !g.configured) return null

  const { groups, max, screens, daily } = built

  // PostHog is unreachable — but the `db` rows come out of our own Postgres and
  // are unaffected, so returning null here threw away good numbers along with
  // the dead ones. Keep those, drop every event row: a dead source rendering 0
  // would read as "nobody used it" when the truth is "we couldn't look".
  // The field is `posthog_error`. It was `g.error` here, which the endpoint has
  // never returned, so this whole branch was unreachable: every PostHog outage
  // rendered as a page of confident zeros — "nobody has got past sign-up onto a
  // real screen yet" — instead of saying we couldn't look. 49 outages logged
  // between Aug 18 and Aug 31.
  if (g.posthog_error) {
    const fromDb = groups
      .map((grp) => ({
        ...grp,
        // The connections note is a sentence about the EVENT funnel, which is
        // exactly what we can't see right now.
        note: grp.spec.note,
        rows: grp.rows.filter((r) => r.kind === "db"),
      }))
      .filter((grp) => grp.rows.length)
    const dbMax = fromDb.length
      ? Math.max(1, ...fromDb.flatMap((x) => x.rows.map((r) => r.people)))
      : 1
    // GeneralCard's chips and first messages come from chat tables, not
    // PostHog — hiding the whole section here threw those away with the
    // event rows. Keep it; it hides its own PostHog half.
    return (
      <div className="space-y-4">
        <p className="text-base text-muted-foreground max-w-5xl">
          <AlertTriangle className="inline size-4 mr-1 -mt-0.5 text-amber-600" />
          <strong className="text-foreground">
            Taps and screens are unavailable — PostHog didn't answer.
          </strong>{" "}
          What's below is counted in our own database instead, so it is still
          correct. Everything that came from event tracking is hidden rather
          than shown as zero.
        </p>
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
          {fromDb.map((grp) => (
            <FeatureCard key={grp.spec.key} grp={grp} max={dbMax} />
          ))}
          <GeneralCard g={g} />
        </div>
      </div>
    )
  }
  const last7 = daily.slice(-7)
  const avg = (pick: (d: { sessions: number; users: number }) => number) =>
    last7.length ? Math.round(last7.reduce((a, d) => a + pick(d), 0) / last7.length) : 0
  const today = daily[daily.length - 1]
  const rage = g.rage
  const use = g.app_use

  return (
    <div className="space-y-4">
      {/*
        PostHog was down, so the backend served the last answer it had. The
        numbers below are real, just old, and saying so is the entire point:
        an aged number presented as current is the same class of lie as a
        blank presented as a zero.
      */}
      <StaleBanner asOf={g.posthog_stale_as_of}>
        Anything counted in our own database is current.
      </StaleBanner>
      <p className="text-base text-muted-foreground max-w-5xl">
        <strong>People</strong> is reach — how many users got there at all.{" "}
        <strong>Total · avg</strong> is intensity — how hard the ones who did lean
        on it. An <strong>outcome</strong> row sits beside the tap it belongs to,
        so the drop between tapping and it working is the thing to read. Every
        real user counts, the team included; only named test accounts are out.{" "}
        {g.days != null && g.days < 365 ? `Last ${g.days} days.` : "All time."}
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/*
          The denominator, stated once at the top so every row below has
          something to be a share OF.

          THREE POPULATIONS, in the dashboard's one vocabulary — "accounts"
          means the middle one and only the middle one:

            waitlist  every row we hold, waitlist prospects included — NOT
                      counted here, it isn't an app number.
            accounts  a Supabase login exists (`roster`, 91 today). The only
                      population PostHog can see: the app sends the user id as
                      its distinct_id after identify(), so an account without
                      one is invisible to every row on this page. That is why
                      the denominator is 91 and not the 53 users — using 53
                      would put people in the numerator who are not in the
                      denominator.
            users     finished onboarding (`users`, 53 today) — the same number
                      the Accounts section prints and the KPI strip divides by.

          72 of the 91 have opened the app, and the 19-account gap is real, not
          a tracking artefact: verified 2026-08-29 against prod, 17 of them are
          the wiped founding members (the Jul-10 wipe blanked their onboarding
          fields, so `is_a_user` no longer recognises accounts that plainly
          used the app — one has 329 screen views), and the rest stopped
          part-way through signup. Four of the wiped 17 have reopened the app
          since the wipe.

          Deliberately not an "app opens" count: the app's own open event fires
          before it knows who it's talking to, and its wake-up event fires again
          every time a notification banner pulls someone back — 94 of those
          collapse into 41 real sessions. Sessions is the honest version, and it
          adds up correctly across the cohort tabs.
        */}
        <div className="rounded-lg border bg-card p-3">
          <div className="text-base text-muted-foreground">Have opened the app</div>
          <div className="text-2xl font-bold tabular-nums">
            {use?.people ?? 0}
            <span className="text-lg font-normal text-muted-foreground">
              {" "}of {use?.roster ?? 0} accounts
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            {use && use.people > 0
              ? `got past sign-up onto a real screen, over ${use.sessions} sessions · ${(use.sessions / use.people).toFixed(1)} each`
              : "nobody has got past sign-up onto a real screen yet"}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Every row below is a share of these {use?.roster ?? 0}, not of the{" "}
            {users ?? "—"} who finished onboarding — an account is only visible
            here once it has a login, so the {use?.roster ?? 0} is the only
            denominator every numerator fits inside.{" "}
            {use && users != null && use.people > users
              ? `The ${use.people - users} who used it without finishing are mostly the wiped founding members: the July wipe blanked their onboarding fields, so nothing on the row says they came in.`
              : ""}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="text-base text-muted-foreground">Typical day</div>
          <div className="text-2xl font-bold tabular-nums">
            {avg((d) => d.sessions)}
            <span className="text-lg font-normal text-muted-foreground"> sessions a day</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {avg((d) => d.users)} people a day · 7-day average
            {today ? ` · ${today.users} today` : ""}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            A session is one visit: the app opened and used until it was put
            down for half an hour. Averaged per day here, totalled over the
            whole window in the tile beside it — same unit, two different sums.
          </div>
        </div>
      </div>

      {daily.length > 1 && (
        <div className="rounded-lg border bg-card p-3">
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null
                    return (
                      <div className="rounded-md border bg-popover px-2 py-1.5 text-xs shadow-xs">
                        <div className="font-medium">{fmtDay(String(label ?? ""))}</div>
                        {payload.map((p) => (
                          <div key={String(p.dataKey)}>
                            {p.name}: {p.value ?? "—"}
                          </div>
                        ))}
                      </div>
                    )
                  }}
                />
                <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="users" name="People" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="inline-flex items-center gap-1.5 mr-3">
              <span className="size-2.5 rounded-sm" style={{ background: "#3b82f6" }} /> Sessions
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ background: "#10b981" }} /> People
            </span>
          </p>
        </div>
      )}

      {/* General goes in immediately before Other, which puts it in the LEFT
       *  column and leaves Other on the right — eight feature cards precede it,
       *  so the pair lands on one row of the two-column grid. */}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
        {groups.flatMap((grp) =>
          grp.spec.key === "other"
            ? [
                <GeneralCard key="general" g={g} />,
                <FeatureCard key={grp.spec.key} grp={grp} max={max} />,
              ]
            : [<FeatureCard key={grp.spec.key} grp={grp} max={max} />],
        )}
      </div>

      {/*
        The "landed here because nothing claims them" sentence used to render at
        this spot — after every card, detached from the rows it was about, so it
        read as unrelated prose. It is now the second half of that card's own
        note, composed in build().
      */}

      <details className="rounded-lg border bg-card p-4">
        <summary className="cursor-pointer text-base font-medium">
          Details — every screen opened, ungrouped
        </summary>
        <p className="text-sm text-muted-foreground mt-2 mb-3">
          Read this when a card above looks wrong: heavy traffic on a screen
          with no matching row means a surface we're blind to. The app reports
          these itself, so nothing here was hand-written. Sign-up and log-in are
          left out.
        </p>
        <table className="w-full text-base">
          <thead className="text-sm text-muted-foreground">
            <tr className="border-b">
              <th className="text-left font-normal py-1">Screen</th>
              <th className="text-right font-normal py-1">People</th>
              <th className="text-right font-normal py-1">Opens</th>
              <th className="text-right font-normal py-1">Each</th>
            </tr>
          </thead>
          <tbody>
            {screens.map((s) => (
              <tr key={s.screen} className="border-b last:border-0">
                <td className="py-1">
                  {s.label || s.screen}
                  <span className="text-muted-foreground text-sm"> · {s.screen}</span>
                </td>
                <td className="text-right tabular-nums py-1">{s.people}</td>
                <td className="text-right tabular-nums py-1">{s.views}</td>
                <td className="text-right tabular-nums py-1 text-muted-foreground">
                  {s.people > 0 ? (s.views / s.people).toFixed(1) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      <FrustrationPanel rage={rage} people={use?.people ?? 0} />
    </div>
  )
}

/** A label + count + proportional bar. The card below has four of these lists
 *  and none of them needs the expandable name drawer `NamedBar` carries. */
function StatRow({
  label,
  value,
  max,
  color,
  note,
}: {
  label: string
  value: number
  max: number
  color: string
  note?: string
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-base truncate">{label}</span>
        <span className="ml-auto text-base font-semibold tabular-nums shrink-0">{value}</span>
        {note && (
          <span className="text-sm text-muted-foreground shrink-0 w-36 text-right truncate">
            {note}
          </span>
        )}
      </div>
      <div className="mt-0.5 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0}%`, background: color }}
        />
      </div>
    </div>
  )
}

// Lives HERE, not in Onboarding Metrics, and renders as the second-to-last card
// of the grid so it sits left of Other. Everything it counts — chat doors, chip
// taps, first messages — is in-app use by people who already have access, which
// is this section's subject and not onboarding's. Two rules kept from its old
// home:
//
// 1. NO SECOND COPY OF FEATURE ENGAGEMENT. Daily sessions/people, the ranked tap
//    list, the ranked screen list and the rage clicks all used to live on this
//    card too. They are gone, not moved twice: the cards above count the same
//    events per PERSON against the roster and group them by feature, which is
//    the more accurate read, and two cards quoting the same events at slightly
//    different totals is how a dashboard loses trust. What stays here is only
//    what the rest of the section does NOT say.
// 2. Each block says WHERE ITS NUMBER COMES FROM. Doors are hand-written
//    captures — autocapture is off on purpose (chat copy lives in accessibility
//    labels, so it would leak message text into event properties), so a door
//    exists only because someone wrote a line for it. Home tiles and the At a
//    Glance card got theirs in RN PR #261, so those rows read zero until that
//    build is on real phones — zero means "not shipped yet", not "nobody
//    tapped". Chips and first messages are read from the chat tables directly.
function GeneralCard({ g }: { g: GeneralMetrics | null }) {
  const [showTyped, setShowTyped] = useState(false)
  if (!g || !g.configured) return null

  const chatOpens = g.posthog_error ? [] : (g.chat_opens ?? [])
  const chips = g.chips ?? []
  const fm = g.first_messages

  const chipMax = Math.max(1, ...chips.map((c) => c.taps))
  const openMax = Math.max(1, ...chatOpens.map((c) => c.count))

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="font-semibold text-2xl">General</h3>
      <ul className="text-base text-muted-foreground mt-1 mb-3 space-y-1 list-disc pl-5">
        <li>
          The rest of in-app use over the last {g.days ?? 30} days — what people
          say to the coach, and how they got to him.
        </li>
        <li>
          Chips and first messages are read out of the chat tables themselves,
          which is why they're wording and not event names.
        </li>
        <li>Our own accounts are out of every number.</li>
      </ul>

      {chatOpens.length > 0 && (
        <>
          <h4 className="font-semibold text-base mb-1">How they reach the coach</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Which door people came through to open chat, counted in{" "}
            <strong>opens</strong>. The Chat card above ranks the same doors by
            how many PEOPLE used each, and files a door it doesn't recognise
            under Other; this list keeps them all in one place. Any open with no
            door on it lands under "somewhere we don't track yet".
          </p>
          <div className="space-y-2 mb-4">
            {chatOpens.map((c) => (
              <StatRow
                key={c.source}
                label={c.label || c.source}
                value={c.count}
                max={openMax}
                color="#06b6d4"
                note={`${c.people} people`}
              />
            ))}
          </div>
        </>
      )}

      {/*
        The two blocks below are the only ones on this card read from our own
        chat tables rather than from event tracking, so they have their own
        failure — `chat_error` — and it used to go unread here: a failed chat
        read printed the "Which prompt they tap" heading over an empty list and
        dropped "What they say first" entirely, which looks exactly like nobody
        having ever spoken to the coach. Same shape of bug as the PostHog banner
        above, one field along.
      */}
      {g.chat_error ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-base">
          <strong>Chat couldn't be read this load.</strong> The prompts users
          tap and what they say first come from our own database, not from event
          tracking — that read failed, so both are hidden rather than shown as
          zero. Everything above is unaffected. Refresh the page.
        </div>
      ) : (
        <>
      <h4 className="font-semibold text-base mb-1">Which prompt they tap</h4>
      <p className="text-sm text-muted-foreground mb-2">
        The canned chips in chat. Tapping one sends its exact words, which is the
        only reason we can count them — the tap itself isn't tracked.
      </p>
      <div className="space-y-2 mb-4">
        {chips.map((c) => (
          <StatRow
            key={c.text}
            label={c.text}
            value={c.taps}
            max={chipMax}
            color="#f59e0b"
            note={c.as_first > 0 ? `${c.as_first} opened with it` : undefined}
          />
        ))}
      </div>

      {fm && fm.users > 0 && (
        <>
          <h4 className="font-semibold text-base mb-1">What they say first</h4>
          <p className="text-sm text-muted-foreground mb-2">
            {fm.users} users have spoken to the coach. <strong>{fm.canned}</strong>{" "}
            opened by tapping a chip; <strong>{fm.typed}</strong> typed something of
            their own — those are the ones worth reading.
          </p>
          {fm.typed > 0 && (
            <button
              type="button"
              onClick={() => setShowTyped((v) => !v)}
              className="text-base underline underline-offset-2 text-muted-foreground hover:text-foreground"
            >
              {showTyped ? "hide" : `read all ${fm.typed}`}
            </button>
          )}
          <div className="mt-2 space-y-2">
            {(showTyped ? fm.samples : fm.samples.slice(0, 3)).map((s, i) => (
              <div key={`${s.name}-${i}`} className="rounded-lg border bg-muted/30 p-2">
                <div className="text-sm font-medium">{s.name}</div>
                <div className="text-base">"{s.text}"</div>
              </div>
            ))}
          </div>
        </>
      )}
        </>
      )}
    </div>
  )
}

/**
 * Where the app fought back — last block of the section, on purpose: it is the
 * cost of everything above it, so it reads after the features, not beside them.
 *
 * A rage click is PostHog's own frustration signal: the same spot jabbed
 * several times in a couple of seconds. Nobody means to do it — it happens when
 * a tap does nothing and the person tries harder. So this is the one number on
 * the page that goes UP when the product is worse.
 *
 * WHAT IT CAN AND CANNOT SAY. The event names no element and no URL, and its
 * own `$screen_name` is a native view class (UI / RNSScreen). The app DOES
 * register its route as `app_screen` (App.tsx:702, RN PR #261) and that lands
 * on every other event — but never on this one, because the SDK raises
 * `$rageclick` from its native layer, which doesn't merge the JS-registered
 * super properties. Waiting for builds to age out was the wrong theory and
 * would have waited forever: over 90 days, 210 of 217 prod jabs had
 * `app_screen` already set EARLIER IN THE SAME SESSION and still carried none.
 *
 * So the backend reads the screen off the session timeline instead — the last
 * `app_screen` seen before the jab, carried forward. ~97% resolve, including
 * every jab already on record. What's left in "unknown" is a session that
 * never stamped a screen at all, not a stale app. Naming the tapped BUTTON
 * would still need touch autocapture, and that stays off on purpose: chat copy
 * lives in accessibility labels, so turning it on would ship member message
 * text into event properties. Screen is the most we will ever get here; the
 * actual jab is in the session recording.
 *
 * WHAT IT COST. The same read also flags jabs the person never came back from.
 * "Session ended on the jab" was the obvious test and it is useless — 2 of 217
 * in 90 days, because the SDK keeps emitting long after the phone is face-down.
 * What works is asking whether anything THEY DID followed: navigation and taps
 * count as staying, `$`-prefixed bookkeeping and `Application …` lifecycle do
 * not, and neither does `chat_v2_run_completed` — the coach reply lands whether
 * or not they are still watching, and it is the single most common event after
 * a jab, so counting it would hide nearly every walk-out.
 */
/** A jab's moment, in the timezone the rest of this page counts days in. */
const JAB_WHEN = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
})

function FrustrationPanel({
  rage,
  people,
}: {
  rage: GeneralMetrics["rage"]
  /** Everyone who opened the app at all — what a frustrated share is OF. */
  people: number
}) {
  if (!rage || rage.taps <= 0) return null
  const screens = rage.screens ?? []
  const named = screens.filter((s) => s.screen !== "unknown")
  const unknown = screens.find((s) => s.screen === "unknown")
  // Jabs only, never people: `walked_people` is per-screen and one person who
  // walked out of two screens would be counted twice by a sum.
  const walkedTaps = screens.reduce((n, s) => n + (s.walked ?? 0), 0)
  const max = Math.max(1, ...named.map((s) => s.taps))
  const share = people > 0 ? Math.round((rage.people / people) * 100) : 0
  // The event can't say WHERE, so the panel says WHEN instead: each jab placed
  // against that person's chat timeline. `jabs` is the chat read's own count,
  // never `rage.taps` — two different reads, and dividing one by the other is
  // the exact mismatch this panel was just fixed for.
  const c = rage.chat
  const jabs = c?.jabs ?? 0
  // The only rows on this card that are single moments rather than counts.
  const recent = rage.jabs ?? []
  const pct = (n: number) => Math.round((n / Math.max(1, jabs)) * 100)

  return (
    <div className="rounded-lg border border-red-300/60 bg-red-500/5 p-4">
      <h3 className="font-semibold text-2xl text-red-600 dark:text-red-400">
        Where people get frustrated
      </h3>
      <p className="text-base text-muted-foreground mt-1 max-w-4xl">
        <strong className="text-foreground tabular-nums">{rage.taps} jabs</strong> from{" "}
        <strong className="text-foreground tabular-nums">{rage.people} people</strong>
        {people > 0 && <> — {share}% of everyone who opened the app</> } ·{" "}
        {(rage.taps / Math.max(1, rage.people)).toFixed(1)} each. A jab is the
        same spot hit again and again because the first tap did nothing. Nobody
        does it on purpose, so this is the one number here that rising means
        something is broken. Kept out of the feature cards above — it isn't use.
      </p>

      {jabs > 0 && (
        <div className="mt-3 max-w-4xl rounded-md border border-red-300/40 bg-background/40 p-3">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <div className="text-2xl font-semibold tabular-nums">{pct(c!.waiting)}%</div>
              <div className="text-sm text-muted-foreground">
                jabbed while waiting on a coach reply
                <span className="tabular-nums"> · {c!.waiting} of {jabs}, {c!.waiting_people} people</span>
              </div>
            </div>
            <div>
              <div className="text-2xl font-semibold tabular-nums">{pct(c!.resends)}%</div>
              <div className="text-sm text-muted-foreground">
                jabbed Send itself, then a message went out
                <span className="tabular-nums"> · {c!.resends} of {jabs}, {c!.resend_people} people</span>
              </div>
            </div>
            <div>
              <div className="text-2xl font-semibold tabular-nums">{pct(c!.mid_chat)}%</div>
              <div className="text-sm text-muted-foreground">
                happened mid-conversation at all
                <span className="tabular-nums"> · {c!.mid_chat} of {jabs}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Read the first number, not the third. Most jabs land inside a chat,
            but the answer had usually already arrived — only {c!.waiting} of the{" "}
            {c!.mid_chat} mid-chat jabs were on a reply that hadn't come back yet.
            "Waiting" means their message went out under 3 minutes earlier and no
            reply had landed; "jabbed Send" means a message went out within 10
            seconds after. Timing, not location — the event still never says
            which screen.
          </p>
        </div>
      )}

      {named.length > 0 ? (
        <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 mt-3 max-w-3xl">
          {named.map((s) => (
            <div key={s.screen} className="contents">
              <div className="min-w-0">
                <div className="text-base truncate">{s.label || s.screen}</div>
                <div className="h-2 rounded-sm bg-red-500/70 relative" style={{ width: `${(s.taps / max) * 100}%` }}>
                  {s.walked > 0 && (
                    <div
                      className="absolute inset-y-0 left-0 rounded-sm bg-red-800"
                      style={{ width: `${(s.walked / Math.max(1, s.taps)) * 100}%` }}
                      title={`${s.walked} of these were the last thing they did`}
                    />
                  )}
                </div>
              </div>
              <div className="text-base tabular-nums text-right self-center">
                {s.taps} <span className="text-muted-foreground text-sm">· {s.people} people</span>
                {s.walked > 0 && (
                  <span className="text-red-700 dark:text-red-400 text-sm"> · {s.walked} left</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {walkedTaps > 0 && (
        <p className="text-sm text-muted-foreground mt-2 max-w-4xl">
          <span className="inline-block align-middle h-2 w-4 rounded-sm bg-red-800 mr-1.5" />
          The darker part of each bar is the jabs they never came back from —{" "}
          <strong className="text-foreground tabular-nums">{walkedTaps}</strong> of{" "}
          <strong className="text-foreground tabular-nums">{rage.taps}</strong>, where nothing
          the person did followed the jab in that session. Navigating counts as
          staying; the coach's reply landing does not, because that happens
          whether or not they are still there. Not the same as the session
          ending — the app keeps sending events after someone puts the phone
          down, so "last event" would find almost nothing.
        </p>
      )}

      {unknown && (
        <p className="text-base text-muted-foreground mt-3 max-w-4xl">
          {named.length === 0 ? (
            <>
              <AlertTriangle className="inline size-4 mr-1 -mt-0.5 text-amber-600" />
              <strong className="text-foreground">
                Not one of these {rage.taps} says which screen it happened on.
              </strong>{" "}
              The screen is reconstructed from each person's session — the
              last one they were on before the jab — so this reads empty only
              when those sessions stamped no screen at all. That is not a stale
              app aging out; it will not fix itself. The honest answer to
              "where" is: we know it's happening, to {rage.people} people, and
              we cannot say where.
            </>
          ) : (
            <>
              Another <strong className="text-foreground tabular-nums">{unknown.taps}</strong> jabs
              from {unknown.people} people happened in sessions that never
              stamped a screen, so there was nothing to carry forward. Staging
              builds are most of it.
            </>
          )}
        </p>
      )}

      <p className="text-sm text-muted-foreground mt-2 max-w-4xl">
        This says which SCREEN someone was standing on, never which button they
        jabbed — naming the button needs touch autocapture, which stays off so
        chat text can't leak into event properties. Which is what the list
        below is for: the recording shows the button.
      </p>

      {recent.length > 0 && (
        <details className="mt-3 max-w-4xl rounded-md border border-red-300/40 bg-background/40 p-3">
          <summary className="cursor-pointer text-base font-medium">
            Watch the last {recent.length} jabs
          </summary>
          <p className="text-sm text-muted-foreground mt-2 mb-3">
            Each one opens PostHog's recording of that session eight seconds
            before the tap, so you see the run-up rather than the aftermath.
            This is the only place the missing answer lives — no property will
            ever name the button, but the video shows it. Newest first.
          </p>
          <div className="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 text-sm">
            {recent.map((j, i) => {
              const row = (
                <>
                  <span className="tabular-nums text-muted-foreground">
                    {JAB_WHEN.format(new Date(j.at))}
                  </span>
                  <span className="truncate">
                    {j.users?.[0] ?? "Unknown member"}
                    <span className="text-muted-foreground"> · {j.label || j.screen}</span>
                  </span>
                  <span className="text-right">
                    {j.walked ? (
                      <span className="text-red-700 dark:text-red-400">left after this</span>
                    ) : (
                      <span className="text-muted-foreground">stayed</span>
                    )}
                  </span>
                </>
              )
              return j.replay ? (
                <a
                  key={`${j.at}-${i}`}
                  href={j.replay}
                  target="_blank"
                  rel="noreferrer"
                  className="contents hover:[&>span]:bg-red-500/10"
                >
                  {row}
                </a>
              ) : (
                <div key={`${j.at}-${i}`} className="contents opacity-60">
                  {row}
                </div>
              )
            })}
          </div>
        </details>
      )}
    </div>
  )
}

function FeatureCard({
  grp,
  max,
}: {
  grp: { spec: FeatureSpec; rows: Row[]; note?: string }
  max: number
}) {
  const { spec, rows } = grp
  const { Icon } = spec
  // The honest header number. Each row carries its OWN distinct-person count
  // and the API gives no overlap between them, so a true union is unknowable
  // here — the biggest row is the floor, and it is labelled as a floor.
  // Missing rows (a DB read that failed) are not a count of zero — include
  // them in Math.max and a failed Connections read next to empty event rows
  // prints "tracked, and genuinely zero" while the row says "couldn't read".
  const unread = rows.some((r) => r.missing)
  const reach = rows.reduce((a, r) => (r.missing ? a : Math.max(a, r.people)), 0)
  // Empty is only a tracking hole if the card can't explain the emptiness.
  const blind = rows.length === 0 && !spec.empty
  const quiet = rows.length === 0 && !!spec.empty

  return (
    <div
      className={
        blind
          ? "rounded-lg border border-dashed border-amber-400/70 bg-amber-500/5 p-4"
          : "rounded-lg border bg-card p-4"
      }
    >
      <div className="flex items-start gap-2">
        <Icon className="size-5 shrink-0 mt-0.5" style={{ color: spec.color }} />
        <div className="min-w-0">
          <h4 className="font-semibold text-xl leading-tight">{spec.name}</h4>
          <div className="text-sm text-muted-foreground">{spec.blurb}</div>
        </div>
        <div className="ml-auto text-right shrink-0">
          {blind ? (
            <span className="text-base font-medium text-amber-700 dark:text-amber-400">
              no tracking
            </span>
          ) : unread && reach === 0 ? (
            <>
              <div className="text-base font-semibold tabular-nums text-muted-foreground">—</div>
              <div className="text-sm text-muted-foreground">couldn't read</div>
            </>
          ) : quiet ? (
            <span className="text-base font-medium text-muted-foreground">
              nothing here
            </span>
          ) : reach === 0 ? (
            // Tracked, and the answer is nobody. Said out loud, because a card
            // that goes quiet here is the finding, not a rendering gap.
            <>
              <div className="text-base font-semibold text-amber-700 dark:text-amber-400">
                nobody touched it
              </div>
              <div className="text-sm text-muted-foreground">tracked, and genuinely zero</div>
            </>
          ) : (
            <>
              <div className="text-base font-semibold tabular-nums">
                {reach} {reach === 1 ? "person" : "people"} reached
              </div>
              {/*
                Was "at least — see note", which now reads as a pointer to the
                card note underneath. Say what the number IS instead.
              */}
              <div className="text-sm text-muted-foreground">
                at least — its biggest single row
              </div>
            </>
          )}
        </div>
      </div>

      {blind ? (
        <p className="text-base text-amber-800 dark:text-amber-300 mt-3">
          <AlertTriangle className="inline size-4 mr-1 -mt-0.5" />
          Shipped, but it sends us nothing — not one event. These numbers are
          missing, not zero: a daily user and someone who never opened it look
          identical from here.
        </p>
      ) : quiet ? (
        <p className="text-sm text-muted-foreground mt-3">{spec.empty}</p>
      ) : (
        <>
          {/*
            ONE grid for the whole card, not one per row. Each row used to own
            its own three-column grid, so the `auto` volume column was sized by
            that row's own text — "none" against "412 total · 6.9 avg" — which
            moved the middle column's edge and left the bars ending at different
            places down a single tile. Sharing the tracks via subgrid is what
            makes every bar in a card start and stop on the same two lines.
          */}
          <div className="grid grid-cols-[3rem_1fr_auto] gap-x-3 gap-y-1.5 mt-3">
            <div className="text-sm text-muted-foreground self-end">people</div>
            <div className="text-sm text-muted-foreground self-end">action</div>
            <div className="text-sm text-muted-foreground self-end text-right">
              volume · intensity
            </div>
            {rows.map((r) => (
              <ActionRow key={r.key} row={r} max={max} color={spec.color} />
            ))}
          </div>
          {grp.note && (
            <p className="text-sm text-muted-foreground mt-3 pt-2 border-t">{grp.note}</p>
          )}
        </>
      )}
    </div>
  )
}

/**
 * One row, and the members behind it.
 *
 * Same deal as the onboarding funnel's bars: hover previews who is in the
 * number, clicking locks the list open so it can be scrolled and read. A
 * count of 8 tells you the feature is quiet; the eight names tell you who to
 * ask why.
 */
function ActionRow({ row, max, color }: { row: Row; max: number; color: string }) {
  const [open, setOpen] = useState(false)
  const zero = !row.missing && row.people === 0 && row.total === 0
  const pct =
    row.missing || max <= 0 ? 0 : Math.max((row.people / max) * 100, row.people > 0 ? 2 : 0)
  const names = row.users
  const nameable = names.length > 0
  // "Read me against the row above" used to be an indent, first on the whole
  // cell (which pushed the bar right and shortened its track) and then on the
  // label (which left the titles starting at two different places down one
  // card). It is now a tick in the gutter: same meaning, nothing moves.
  const tied = row.kind === "door" || row.sub
  return (
    <>
      <button
        type="button"
        disabled={!nameable}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={nameable ? open : undefined}
        title={nameable ? "Click to see who" : undefined}
        className={cn(
          // gap-x restated: subgrid inherits the parent's gutters, but spelling
          // it out here means the row survives someone moving the gap around.
          "col-span-3 grid grid-cols-subgrid gap-x-3 items-center text-left group relative",
          nameable && "cursor-pointer",
        )}
      >
        <div
          className={
            "text-2xl font-semibold tabular-nums text-right leading-none" +
            (zero || row.missing ? " text-muted-foreground/60" : "")
          }
          title={row.missing ? "This read failed — not a count of zero." : undefined}
        >
          {row.missing ? "—" : row.people}
        </div>
        <div className="min-w-0 relative">
          <div className="flex items-baseline gap-1.5">
            {tied && (
              <span
                aria-hidden
                className="absolute -left-2 top-2 h-px w-1.5 bg-muted-foreground/40"
              />
            )}
            <span className="text-base leading-tight">{row.label}</span>
            {row.kind === "screen" && (
              <span
                className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0"
                title="Auto-captured screen open — the app reports this for everyone with no tracking code. A tap row means someone wrote a line for it."
              >
                screen
              </span>
            )}
            {row.kind === "db" && (
              <span
                className="text-[11px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400 shrink-0"
                title="Counted in our own database, not PostHog — this is the outcome that actually happened, so it is not limited to app builds that ship the tracking."
              >
                database
              </span>
            )}
            {row.kind === "outcome" && (
              <span
                className="text-[11px] uppercase tracking-wide text-cyan-600 dark:text-cyan-400 shrink-0"
                title="What happened, not what somebody tapped — a success, a failure, or something the app put in front of them. Read it against the tap above it: the gap between the two is the drop."
              >
                outcome
              </span>
            )}
            {nameable && (
              // Floated over the label line rather than laid out beside it: a
              // reserved 90px slot would wrap the longer labels on every row,
              // permanently, to hint at something only visible on hover.
              <span className="pointer-events-none absolute right-0 top-0 pl-8 bg-card text-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
                {open ? "hide names" : "show names"}
              </span>
            )}
          </div>
          <div
            className={cn(
              "mt-0.5 h-2.5 rounded-full bg-muted overflow-hidden ring-offset-1",
              open
                ? "ring-2 ring-ring"
                : nameable && "group-hover:ring-1 group-hover:ring-border",
            )}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${pct}%`, background: color, opacity: row.kind === "screen" ? 0.55 : 1 }}
            />
          </div>
          {row.note && (
            <div className="text-sm text-muted-foreground mt-1 leading-snug">{row.note}</div>
          )}
          {nameable && !open && (
            <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 w-80 rounded-lg border bg-popover p-3 shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
              {/* Capped, not scrolled. This panel is pointer-events-none so the
               *  row underneath stays clickable, which also means a scrollbar
               *  in here can't be dragged — it looked like the rest of the
               *  names were reachable when they weren't. Clicking the row
               *  opens the full list below, which does scroll. */}
              <NameList title={row.label} names={names} tone="good" limit={10} />
            </div>
          )}
        </div>
        <div className="text-base tabular-nums text-muted-foreground text-right shrink-0">
          {row.missing
            ? "couldn't read"
            : zero
              ? "none"
              : row.kind === "db"
                ? // Only where a person can hold more than one. A calendar is
                  // one each, so that row says nothing rather than "1.0 each".
                  row.unit && row.total > row.people
                  ? `${row.total} ${row.unit} · ${(row.total / Math.max(row.people, 1)).toFixed(1)} each`
                  : ""
                : `${row.total} total · ${(row.total / Math.max(row.people, 1)).toFixed(1)} avg`}
        </div>
      </button>
      {open && (
        <div className="col-span-3 rounded-lg border bg-muted/30 p-3">
          <NameList title={row.label} names={names} tone="good" />
        </div>
      )}
    </>
  )
}

/** Resolve the static map against whatever the API actually returned. */
function build(g: GeneralMetrics | null) {
  const actions = g?.actions ?? []
  const screens = g?.screens ?? []
  const doors = g?.chat_opens ?? []
  const daily = g?.daily ?? []
  const outcomes = g?.outcomes ?? []

  const byAction = new Map(actions.map((a) => [a.event, a]))
  const byScreen = new Map(screens.map((s) => [s.screen, s]))
  const byDoor = new Map(doors.map((d) => [d.source, d]))
  const byOutcome = new Map(outcomes.map((o) => [o.event, o]))

  // Rows read from Postgres rather than PostHog, keyed by the id the card
  // asks for. Absent from this map means the read FAILED — which is why a db
  // row can render "—" while every event row renders 0. For events, 0 is a
  // real answer; for these, 0 would claim nobody linked anything when what
  // actually happened is that we couldn't look.
  const byDb = new Map<string, { people: number; total: number; users: string[] }>()
  if (g?.connected_wearables) {
    const cw = g.connected_wearables
    byDb.set("connected_wearables", {
      people: cw.people,
      // Someone can connect a watch AND a ring, so devices >= people.
      total: cw.devices,
      users: cw.users ?? [],
    })
  }
  for (const la of g?.linked_accounts ?? []) {
    byDb.set(la.id, {
      people: la.people,
      total: la.accounts ?? la.people,
      users: la.users ?? [],
    })
  }
  // Screens whose only honest home is the diagnostic table at the bottom.
  // Without this they fall into Other and get flagged as needing a home, which
  // is how the `Center` screen ended up posing as a feature row in the first
  // place. `Center` is the app switching the highlighted tab to the coach slot
  // ON THE MEMBER'S BEHALF to hand off a job — six code paths (five of them a
  // booking or an edit), indistinguishable inside one count. The paths worth
  // reading are already read better elsewhere: the home Book pill is
  // `home_book_tapped`, and everyone who actually reached the coach, by any
  // route, is `chat_v2_run_completed`. Its label also invited the one reading
  // it never supported — a tap on the coach tab, which is the single thing it
  // cannot contain.
  //
  // `Workouts` is here for the reason spelled out on the Workouts card: it is
  // the tab the app LANDS on, so its people-count is everyone who opened the
  // app at all — 72, the same 72 as the header strip above. Keeping it out of
  // Workouts without keeping it out of the spill just moved the problem: it
  // fell into Other, sorted to the top on 1,540 views, and set that card's
  // headline, so a card about retired sign-up screens opened by reporting the
  // single most-used screen in the app. The honest home for it is the
  // ungrouped screen table at the bottom, which is not filtered by this set.
  const SUPPRESSED = ["screen:Center", "screen:Workouts"]
  const claimed = new Set<string>(SUPPRESSED)

  // Zero is an answer, so a mapped row ALWAYS renders. Rows used to disappear
  // when nothing hit them, which made "this cohort ignored the feature" — the
  // most useful thing a cohort tab can tell us — look identical to blank space.
  // The two silences that remain distinguishable: a row reading 0 is a feature
  // people didn't use; a whole card reading "no tracking" is one we can't see.
  const resolve = (r: RowSpec): Row => {
    // Read straight from Postgres, not PostHog — there is no event for this,
    // or the event under-counts an outcome the table records exactly.
    if (r.kind === "db") {
      const hit = byDb.get(r.id)
      return {
        key: `db:${r.id}`,
        kind: "db",
        label: r.label || r.id,
        people: hit?.people ?? 0,
        total: hit?.total ?? 0,
        // Not the same silence as a 0. See `missing` on Row.
        missing: !hit,
        unit: r.unit,
        note: r.note,
        sub: r.sub,
        lead: r.lead,
        users: hit?.users ?? [],
      }
    }
    const hit =
      r.kind === "action"
        ? byAction.get(r.id)
        : r.kind === "screen"
          ? byScreen.get(r.id)
          : r.kind === "outcome"
            ? byOutcome.get(r.id)
            : byDoor.get(r.id)
    claimed.add(`${r.kind}:${r.id}`)
    return {
      key: `${r.kind}:${r.id}`,
      kind: r.kind,
      label: r.label || hit?.label || r.id,
      people: hit?.people ?? 0,
      total: hit ? ("count" in hit ? hit.count : hit.views) : 0,
      note: r.note,
      sub: r.sub,
      lead: r.lead,
      users: hit?.users ?? [],
    }
  }

  const groups = FEATURES.map((spec) => {
    const rows: Row[] = spec.rows.map(resolve)
    // `lead` first, then size. A card's headline outcome is usually its
    // SMALLEST number — the taps leading up to it always outnumber it — so
    // sorting on people alone buries the one row the card is about.
    rows.sort(
      (a, b) =>
        (b.lead ? 1 : 0) - (a.lead ? 1 : 0) || b.people - a.people || b.total - a.total,
    )
    for (const r of spec.rows) {
      if (!r.after) continue
      const i = rows.findIndex((x) => x.key === `${r.kind}:${r.id}`)
      const j = rows.findIndex((x) => x.key.endsWith(`:${r.after}`))
      if (i < 0 || j < 0) continue
      const [moved] = rows.splice(i, 1)
      rows.splice(j < i ? j + 1 : j, 0, moved)
    }
    // The funnel sentence has to be counted per PERSON, and it cannot be
    // subtraction on the rows above: one member tapped 20 times, failed 11 and
    // eventually succeeded, so tapped − connected − failed lands on 1 silent
    // drop where there are really 3. The backend groups by person first.
    const note =
      spec.key === "connections" ? joinNotes(funnelSentence(g), spec.note) : spec.note
    return { spec, rows, note }
  })

  // Nothing gets dropped: anything the API sent that no feature claims falls
  // into Other. A new event shipping from the app shows up here on its own
  // rather than vanishing until someone remembers to edit this file.
  const other = groups.find((x) => x.spec.key === "other")!
  const unmappedNote: string[] = []
  const spill = (
    kind: Kind,
    id: string,
    label: string,
    people: number,
    total: number,
    users: string[] = [],
  ) => {
    if (claimed.has(`${kind}:${id}`) || total <= 0) return
    const retired = kind === "screen" ? RETIRED_SIGNUP_SCREENS[id] : undefined
    other.rows.push({
      key: `${kind}:${id}`,
      kind,
      label: retired ?? label,
      people,
      total,
      users,
    })
    // A retired screen is not unclassified — it has a home, the home is "gone".
    // Listing it as something to find a feature for is how it read before.
    if (!retired) unmappedNote.push(label)
  }
  for (const a of actions)
    spill("action", a.event, a.label || a.event, a.people, a.count, a.users ?? [])
  for (const s of screens)
    spill("screen", s.screen, s.label || s.screen, s.people, s.views, s.users ?? [])
  for (const d of doors)
    spill("door", d.source, d.label || d.source, d.people, d.count, d.users ?? [])
  for (const o of outcomes)
    spill("outcome", o.event, o.label || o.event, o.people, o.count, o.users ?? [])
  other.rows.sort((a, b) => b.people - a.people || b.total - a.total)
  // Composed here, not on the spec, because half of it depends on what actually
  // spilled on THIS tab. The card used to carry a fixed "mostly the onboarding
  // questions" line while the reason a row was in it lived in a paragraph below
  // every card — so the one sentence that explained these rows was the one
  // sentence not attached to them.
  const retiredCount = other.rows.filter((r) =>
    r.key.startsWith("screen:") ? RETIRED_SIGNUP_SCREENS[r.key.slice(7)] : false,
  ).length
  other.note = joinNotes(
    retiredCount > 0
      ? `Each row is one screen of the activity questionnaire we used to run at sign-up, labelled with the question it asked. The app deleted these screens on 2026-07-04, so the count is how many users answered that question BEFORE then — nobody can reach one now. Running and weightlifting are filtered out upstream, so this is most of that flow, not all of it.`
      : undefined,
    unmappedNote.length > 0
      ? `Also here because nothing above claims them: ${unmappedNote.join(", ")}. Those are live and worth a home in a feature card.`
      : undefined,
  )

  // One scale for every bar in every card — a 3-person row must LOOK small next
  // to an 87-person one, or the cards silently rescale and read as equals.
  const max = Math.max(1, ...groups.flatMap((x) => x.rows.map((r) => r.people)))
  return { groups, max, screens, daily }
}

/**
 * "Did the calendar connect actually work" in one sentence, per person.
 *
 * A bare success rate would hide the worst bucket. Someone who tapped and got
 * an error at least knows; someone who tapped and got NOTHING back — no
 * success, no failure — is a member sitting in front of a button that did
 * nothing, and that is the group worth chasing.
 */
function funnelSentence(g: GeneralMetrics | null): string | undefined {
  const f = g?.calendar_funnel
  if (!f || f.tapped <= 0) return undefined
  const pct = Math.round((f.connected / f.tapped) * 100)
  const parts = [
    `Of the ${f.tapped} ${f.tapped === 1 ? "person" : "people"} who tapped connect calendar, ${f.connected} ended up connected (${pct}%).`,
  ]
  if (f.failed_only > 0) parts.push(`${f.failed_only} hit an error and never got through.`)
  if (f.silent > 0) {
    parts.push(
      `${f.silent} tapped and the app never reported back either way — no success, no error.`,
    )
  }
  if (f.failed_only === 0 && f.silent === 0) parts.push("Nobody dropped.")
  return parts.join(" ")
}

/**
 * The old activity questionnaire, keyed by the screen name PostHog still holds.
 *
 * These screens do not exist any more: offseason-rn deleted them on 2026-07-04
 * (`fb269fd`, "delete the legacy funnel (56 screens)"), shipped in the current
 * 1.9.1 build. So nobody signing up today can reach one, and every number on
 * these rows is somebody who signed up BEFORE that build — a record of a flow
 * we removed, never a feature in use. On the All tab the window is all time,
 * which is the only reason they still have counts at all.
 *
 * The label is each screen's OWN question, copied out of the deleted source, so
 * a bar can be read without a decoder ring — `SwimmingExample` told the reader
 * nothing, and "an example of a challenging but doable set" is the thing that
 * was actually asked. Same rule as the onboarding funnel's step labels.
 *
 * Running and weightlifting are missing on purpose and not by ours: the backend
 * already filters `Running`/`RunningStyle`/`RunningExample`/`Weightlifting*` out
 * as sign-up noise (`_GATE_SCREENS`), and their follow-ups were never added to
 * that list. That asymmetry is why the card cannot show the whole questionnaire.
 */
const RETIRED_SIGNUP_SCREENS: Record<string, string> = {
  Walking: "Where are you walking or hiking?",
  WalkingDistance: "How far or long do you walk or hike?",
  RunningDistance: "How far or long do you typically go per run?",
  Swimming: "Where do you swim?",
  SwimmingStyle: "Do you swim intervals, for distance, or both?",
  SwimmingExample: "An example of a challenging but doable set",
  Yoga: "Do you have a yoga studio membership?",
  Pilates: "Do you have a pilates studio membership?",
  Sports: "What sport(s) do you play?",
  SportsType: "Are you in a league or do you play pick-up?",
  Other: 'You chose "other" earlier — what did we miss?',
  AnythingElse: "Anything else about your routine & preferences?",
}

function joinNotes(...notes: (string | undefined)[]): string | undefined {
  const kept = notes.filter(Boolean)
  return kept.length ? kept.join(" ") : undefined
}

function fmtDay(iso: string): string {
  const d = new Date(iso + "T00:00:00")
  return `${d.getMonth() + 1}/${d.getDate()}`
}
